import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { ClientService } from '@vanilla/services/ClientService.js';
import { ContactService } from '@vanilla/services/ContactService.js';
import { AccountContactService } from '@vanilla/services/AccountContactService.js';
import { UserService } from '@vanilla/services/UserService.js';
import { UsersApiService } from '@vanilla/services/crm/UsersApiService.js';
import { INDUSTRIES } from '@vanilla/constants/Industries.js';
import { AppConfigService } from '@vanilla/services/AppConfigService.js';
import { isValidEmail, EMAIL_ERROR_MESSAGE } from '@vanilla/utils/EmailValidation.js';
import { confirmAccountNameNotDuplicate } from '@/lib/confirmAccountNameNotDuplicate.js';
import { toastManager } from '@/lib/toast.js';
import { acquireQcPopupOpen, releaseQcPopupOpen } from '@/lib/qcPopupOpen.js';
import { permissions } from '@vanilla/services/PermissionService.js';
import { QC_EDIT_ICON, QC_PLUS_ICON } from '@/lib/quickCreateHelpers.js';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard.js';

/**
 * React QuickCreateDialog — primary QC UI for React surfaces (Radix Dialog).
 */

function qcContactLabel(c) {
  return `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || c.id;
}

/** Primary contact select + nested contact QC (mirrors vanilla account QC). */
function AccountPrimaryContactField({ value, onChange, accountId }) {
  const [contacts, setContacts] = useState([]);
  const [nestedOpen, setNestedOpen] = useState(false);
  const [nestedEdit, setNestedEdit] = useState(null);
  const canCreate = permissions.canCreate('contacts');
  const canEdit = permissions.canEdit('contacts');

  useEffect(() => {
    ContactService.getAll()
      .then((list) => setContacts(Array.isArray(list) ? list : []))
      .catch(() => setContacts([]));
  }, []);

  const upsertContact = (entity) => {
    if (!entity?.id) return;
    setContacts((prev) => {
      const idx = prev.findIndex((c) => c.id === entity.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...entity };
        return next;
      }
      return [...prev, entity];
    });
    onChange(entity.id);
  };

  const stopBubble = (e) => e.stopPropagation();

  const onAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canCreate) {
      toastManager.error('Permission denied', "You don't have permission to create contacts.");
      return;
    }
    setNestedEdit(null);
    setNestedOpen(true);
  };

  const onEdit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!value || !canEdit) return;
    let editEntity = contacts.find((c) => c.id === value) || null;
    try {
      editEntity = await ContactService.getById(value) || editEntity;
    } catch {
      /* keep list copy */
    }
    setNestedEdit(editEntity);
    setNestedOpen(true);
  };

  const showButtons = canCreate || canEdit;

  return (
    <>
      <div className="quick-create-field">
        <label htmlFor="qc-account-primary-contact">Primary contact</label>
        {showButtons ? (
          <div className="select-with-icon quick-create-wrapper">
            <div className="deal-form-select-grow" style={{ flex: 1, minWidth: 0 }}>
              <NativeSelect
                id="qc-account-primary-contact"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
              >
                <option value="">—</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{qcContactLabel(c)}</option>
                ))}
              </NativeSelect>
            </div>
            <div className="quick-create-btn-group">
              {canCreate && (
                <button
                  type="button"
                  className="quick-create-btn quick-create-add-btn"
                  data-entity-type="contact"
                  aria-label="Create new contact"
                  title="Create new contact"
                  dangerouslySetInnerHTML={{ __html: QC_PLUS_ICON }} // eslint-disable-line react/no-danger -- static SVG
                  onPointerDown={stopBubble}
                  onClick={onAdd}
                />
              )}
              {canEdit && (
                <button
                  type="button"
                  className="quick-create-btn quick-create-edit-btn"
                  data-entity-type="contact"
                  aria-label="Edit contact"
                  title="Edit contact"
                  disabled={!value}
                  dangerouslySetInnerHTML={{ __html: QC_EDIT_ICON }} // eslint-disable-line react/no-danger -- static SVG
                  onPointerDown={stopBubble}
                  onClick={onEdit}
                />
              )}
            </div>
          </div>
        ) : (
          <NativeSelect
            id="qc-account-primary-contact"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">—</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>{qcContactLabel(c)}</option>
            ))}
          </NativeSelect>
        )}
      </div>
      <QuickCreateDialog
        open={nestedOpen}
        onOpenChange={setNestedOpen}
        entityType="contact"
        editEntity={nestedEdit}
        hideAccountField
        preselectedClientId={accountId || null}
        stackLevel={1}
        onSave={async (entity) => {
          if (accountId && entity?.id) {
            try {
              await AccountContactService.link(accountId, entity.id);
            } catch (err) {
              console.warn('Failed to link contact to account:', err);
            }
          }
          upsertContact(entity);
        }}
      />
    </>
  );
}

function AccountFields({ form, setField, errors, accountId }) {
  return (
    <>
      <div className="quick-create-field">
        <label htmlFor="qc-account-name">Account name <span className="required-asterisk">*</span></label>
        <Input id="qc-account-name" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Enter account name" />
        {errors.name && <span className="field-error">{errors.name}</span>}
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-account-phone">Phone</label>
        <Input id="qc-account-phone" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="" />
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-account-website">Website</label>
        <Input id="qc-account-website" value={form.website} onChange={(e) => setField('website', e.target.value)} placeholder="https://example.com" />
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-account-industry">Industry <span className="required-asterisk">*</span></label>
        <NativeSelect id="qc-account-industry" value={form.industry} onChange={(e) => setField('industry', e.target.value)}>
          <option value="">Select…</option>
          {Object.entries(INDUSTRIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </NativeSelect>
        {errors.industry && <span className="field-error">{errors.industry}</span>}
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-account-email">Email <span className="required-asterisk">*</span></label>
        <Input id="qc-account-email" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="info@company.com" />
        {errors.email && <span className="field-error">{errors.email}</span>}
      </div>
      <AccountPrimaryContactField
        value={form.primaryContactId}
        onChange={(id) => setField('primaryContactId', id)}
        accountId={accountId}
      />
    </>
  );
}

function ContactFields({ form, setField, errors, hideAccountField: _hideAccountField }) {
  return (
    <>
      <div className="quick-create-field">
        <label htmlFor="qc-contact-firstName">First name <span className="required-asterisk">*</span></label>
        <Input id="qc-contact-firstName" value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} />
        {errors.firstName && <span className="field-error">{errors.firstName}</span>}
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-contact-lastName">Last name</label>
        <Input id="qc-contact-lastName" value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} />
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-contact-email">Email <span className="required-asterisk">*</span></label>
        <Input id="qc-contact-email" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
        {errors.email && <span className="field-error">{errors.email}</span>}
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-contact-phone">Phone</label>
        <Input id="qc-contact-phone" value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-contact-title">Job title</label>
        <Input id="qc-contact-title" value={form.title} onChange={(e) => setField('title', e.target.value)} />
      </div>
    </>
  );
}

function UserFields({ form, setField, errors, isEdit }) {
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    UsersApiService.listRoles({ assignableOnly: true }).then((res) => {
      const list = (res?.data ?? res ?? []).filter((r) => r.is_system !== true);
      setRoles(list);
    }).catch(() => {});
  }, []);
  return (
    <>
      <div className="quick-create-field">
        <label htmlFor="qc-user-name">Name <span className="required-asterisk">*</span></label>
        <Input id="qc-user-name" value={form.name} onChange={(e) => setField('name', e.target.value)} />
        {errors.name && <span className="field-error">{errors.name}</span>}
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-user-email">Email <span className="required-asterisk">*</span></label>
        <Input id="qc-user-email" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
        {errors.email && <span className="field-error">{errors.email}</span>}
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-user-role">Role <span className="required-asterisk">*</span></label>
        <NativeSelect id="qc-user-role" value={form.role} onChange={(e) => setField('role', e.target.value)}>
          <option value="">Select role…</option>
          {roles.map((r) => {
            const v = r?.name ?? r?.value ?? '';
            const l = r?.description ?? v;
            return <option key={v} value={v}>{l}</option>;
          })}
        </NativeSelect>
        {errors.role && <span className="field-error">{errors.role}</span>}
      </div>
      <div className="quick-create-field">
        <label htmlFor="qc-user-password">Password {isEdit ? '' : <span className="required-asterisk">*</span>}</label>
        <Input id="qc-user-password" type="password" value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder={isEdit ? 'Leave blank to keep current' : ''} />
        {errors.password && <span className="field-error">{errors.password}</span>}
        <small className="field-hint">{AppConfigService.isAzureAdEnabled() ? 'Optional — set for email/password login' : 'Required for new users'}</small>
      </div>
    </>
  );
}

export function QuickCreateDialog({
  open,
  onOpenChange,
  entityType,
  onSave,
  editEntity,
  hideAccountField,
  preselectedClientId = null,
  stackLevel = 0,
}) {
  const isEdit = !!editEntity;
  const [form, setForm] = useState({
    name: '', phone: '', website: '', industry: '', email: '',
    firstName: '', lastName: '', title: '', role: 'sales_executive', password: '',
    primaryContactId: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState('');
  const closingRef = useRef(false);

  useLayoutEffect(() => {
    if (!open) return undefined;
    acquireQcPopupOpen();
    return () => releaseQcPopupOpen();
  }, [open]);

  useEffect(() => {
    let next = {
      name: '', phone: '', website: '', industry: '', email: '',
      firstName: '', lastName: '', title: '', role: 'sales_executive', password: '',
      primaryContactId: '',
    };
    if (editEntity) {
      if (entityType === 'account') {
        next = {
          name: editEntity.name || '',
          phone: editEntity.phone || '',
          website: editEntity.website || '',
          industry: editEntity.industry || '',
          email: editEntity.email || '',
          firstName: '', lastName: '', title: '', role: '', password: '',
          primaryContactId: editEntity.primaryContactId || editEntity.primary_contact_id || '',
        };
      } else if (entityType === 'contact') {
        next = {
          name: '', phone: editEntity.phone || '', website: '', industry: '',
          email: editEntity.email || '',
          firstName: editEntity.firstName || '', lastName: editEntity.lastName || '',
          title: editEntity.title || '', role: '', password: '', primaryContactId: '',
        };
      } else if (entityType === 'user') {
        next = {
          name: editEntity.name || '', phone: editEntity.phone || '', website: '', industry: '',
          email: editEntity.email || '', firstName: '', lastName: '', title: '',
          role: editEntity.role || 'sales_executive', password: '', primaryContactId: '',
        };
      }
    }
    setForm(next);
    setErrors({});
    setBaseline(JSON.stringify(next));
  }, [editEntity, entityType, open]);

  const isDirty = useMemo(() => {
    if (!open || !baseline) return false;
    return JSON.stringify(form) !== baseline;
  }, [open, form, baseline]);
  const { requestClose } = useUnsavedGuard(isDirty);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const closeSafely = async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    try {
      await requestClose(() => onOpenChange(false));
    } finally {
      closingRef.current = false;
    }
  };

  const handleOpenChange = (next) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    void closeSafely();
  };

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const validate = () => {
    const e = {};
    if (entityType === 'account') {
      if (!form.name.trim()) e.name = 'Account name is required.';
      if (!form.industry) e.industry = 'Industry is required.';
      if (!form.email.trim()) e.email = 'Email is required.';
      else if (!isValidEmail(form.email)) e.email = EMAIL_ERROR_MESSAGE;
    } else if (entityType === 'contact') {
      if (!form.firstName.trim()) e.firstName = 'First name is required.';
      if (!form.email.trim()) e.email = 'Email is required.';
      else if (!isValidEmail(form.email)) e.email = EMAIL_ERROR_MESSAGE;
    } else if (entityType === 'user') {
      if (!form.name.trim()) e.name = 'Name is required.';
      if (!form.email.trim()) e.email = 'Email is required.';
      else if (!isValidEmail(form.email)) e.email = EMAIL_ERROR_MESSAGE;
      if (!form.role) e.role = 'Role is required.';
      if (!AppConfigService.isAzureAdEnabled() && !isEdit && !String(form.password || '').trim()) e.password = 'Password is required for new users.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (entityType === 'account' && !isEdit) {
      const proceed = await confirmAccountNameNotDuplicate(form.name);
      if (!proceed) return;
    }
    setSaving(true);
    try {
      let saved;
      if (entityType === 'account') {
        const data = {
          name: form.name.trim(),
          phone: form.phone.trim(),
          website: form.website.trim(),
          industry: form.industry,
          email: form.email.trim(),
          primaryContactId: form.primaryContactId || null,
        };
        saved = editEntity ? await ClientService.save({ ...editEntity, ...data }) : await ClientService.save(data);
      } else if (entityType === 'user') {
        const data = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), role: form.role, password: form.password.trim() || undefined };
        saved = editEntity ? await UserService.save({ ...editEntity, ...data }) : await UserService.save(data);
      } else {
        const data = { firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim(), phone: form.phone.trim(), title: form.title.trim() };
        const linkAccountId = preselectedClientId || null;
        saved = await ContactService.save({ ...(editEntity || {}), ...data }, linkAccountId || undefined);
      }
      toastManager.success(`${entityType} ${isEdit ? 'updated' : 'created'}`, `${entityType} has been ${isEdit ? 'updated' : 'created'} successfully.`);
      onSave?.(saved);
      onOpenChange(false);
    } catch (err) {
      toastManager.crmError('Save', entityType, err);
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit
    ? (entityType === 'account' ? 'Edit Account' : entityType === 'contact' ? 'Edit Contact' : 'Edit Sales Representative')
    : (entityType === 'account' ? 'Create Account' : entityType === 'contact' ? 'Create Contact' : 'Sales Representative');

  const contentZ = stackLevel > 0 ? 'z-[1601]' : 'z-[1501]';
  const overlayZ = stackLevel > 0 ? 'z-[1600] bg-black/50' : 'z-[1500] bg-black/50';

  const guardDismiss = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('.quick-create-btn, .quick-create-btn-group')) {
      e.preventDefault();
      return;
    }
    if (stackLevel === 0 && document.querySelector('[data-qc-stack="1"]')) {
      e.preventDefault();
      return;
    }
    if (document.querySelector('[role="alertdialog"]')) {
      e.preventDefault();
      return;
    }
    if (isDirtyRef.current) {
      e.preventDefault();
      void closeSafely();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`sm:max-w-[420px] ${contentZ}`}
        overlayClassName={overlayZ}
        aria-describedby={undefined}
        data-qc-stack={String(stackLevel)}
        onOpenAutoFocus={(e) => {
          const first = e.currentTarget.querySelector('input, select, textarea, button');
          if (first instanceof HTMLElement) {
            e.preventDefault();
            first.focus();
          }
        }}
        onPointerDownOutside={guardDismiss}
        onInteractOutside={guardDismiss}
        onEscapeKeyDown={(e) => {
          if (stackLevel === 0 && document.querySelector('[data-qc-stack="1"]')) {
            e.preventDefault();
            return;
          }
          if (document.querySelector('[role="alertdialog"]')) {
            e.preventDefault();
            return;
          }
          if (isDirtyRef.current) {
            e.preventDefault();
            void closeSafely();
          }
        }}
        onFocusOutside={(e) => {
          if (stackLevel === 0 && document.querySelector('[data-qc-stack="1"]')) e.preventDefault();
          else if (document.querySelector('[role="alertdialog"]')) e.preventDefault();
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription className="sr-only">Quick create {entityType}</DialogDescription>
        <form onSubmit={handleSave} noValidate className="space-y-4">
          {entityType === 'account' && (
            <AccountFields
              form={form}
              setField={setField}
              errors={errors}
              accountId={editEntity?.id || null}
            />
          )}
          {entityType === 'contact' && <ContactFields form={form} setField={setField} errors={errors} hideAccountField={hideAccountField} />}
          {entityType === 'user' && <UserFields form={form} setField={setField} errors={errors} isEdit={isEdit} />}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => void closeSafely()}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
